import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import useConsentStore from '../store/consentStore';
import type { Company } from '../utils/Types/ConsentTypes';

const ICO_PARAM = 'ico';

export const useCompanySelection = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    companies,
    selectedIco,
    setSelectedIco,
  } = useConsentStore();

  // Get ico from URL
  const icoFromUrl = searchParams.get(ICO_PARAM);

  // Sync URL -> Store
  useEffect(() => {
    if (icoFromUrl && icoFromUrl !== selectedIco) {
      // Check if ico exists in companies list
      const companyExists = companies.some(c => c.ico === icoFromUrl);
      if (companyExists) {
        setSelectedIco(icoFromUrl);
      } else if (companies.length > 0) {
        // If ico from URL not found, set first company (preserve other params)
        const newParams = new URLSearchParams(searchParams);
        newParams.set(ICO_PARAM, companies[0].ico);
        setSearchParams(newParams, { replace: true });
      }
    }
  }, [icoFromUrl, selectedIco, companies, setSelectedIco, setSearchParams, searchParams]);

  // Set first company as default
  useEffect(() => {
    if (companies.length > 0 && !icoFromUrl) {
      // Preserve other params when setting ico
      const newParams = new URLSearchParams(searchParams);
      newParams.set(ICO_PARAM, companies[0].ico);
      setSearchParams(newParams, { replace: true });
    }
  }, [companies, icoFromUrl, setSearchParams, searchParams]);

  // Change company function
  const selectCompany = useCallback((ico: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set(ICO_PARAM, ico);
    setSearchParams(newParams, { replace: true });
    setSelectedIco(ico);
  }, [setSearchParams, setSelectedIco, searchParams]);

  // Get current company
  const selectedCompany: Company | undefined = companies.find(c => c.ico === selectedIco);

  return {
    companies,
    selectedIco,
    selectedCompany,
    selectCompany,
    hasMultipleCompanies: companies.length > 1,
  };
};

export default useCompanySelection;

